require("./vouchers-print-page.scss");

import {AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, input} from "@angular/core";
import {TrainContainerDetails} from "../../../models/train-container-details.model";
import {TrainVoucher} from "../../models/train-voucher.model";
import {SplitService} from "../../../../commons/services/split-service.service";
import {PRINT_DELAY_MS} from "../../constants/threepl-train.constant";
import * as moment from "moment";

export interface PrintedContainer extends TrainContainerDetails {
  allowedWeightInTon?: number;
  weight: number;
  length: number;
  lockNumber?: string;
  riskCode?: string;
}

export interface PrintTotals {
  totalWeight: number;
  empty20: number;
  empty40: number;
  full20: number;
  full40: number;
  totalCarts: number;
}

export function toPrintedContainers(voucher: TrainVoucher): PrintedContainer[] {
  const items = voucher.items ?? [];
  const voucherWeight = items.reduce((sum, item) => sum + (item?.weight || 0), 0);
  const voucherLength = items.reduce((sum, item) => sum + (item?.dimensions?.length || 0), 0);

  return (voucher.threePlExtraData.carsDetails ?? []).flatMap((car) =>
    (car.trainContainerDetails ?? []).map((container) => {
      const printed: PrintedContainer = {
        ...container,
        carType: car.carType,
        allowedWeightInTon: car.allowedWeightInTon,
        carId: `${car.id} ${car.allowedWeightInTon} ${car.carType}`,
        weight: voucherWeight,
        length: voucherLength,
      };
      (voucher.orderIdMetaData?.[container.omsOrderId] ?? []).forEach((metadata) => {
        if (metadata.key === "lockNumber") {
          printed.lockNumber = metadata.value;
        }
        if (metadata.key === "riskCode") {
          printed.riskCode = metadata.value;
        }
      });
      return printed;
    }));
}

export function toPrintTotals(containers: PrintedContainer[]): PrintTotals {
  const count = (empty: boolean, twenty: boolean) =>
    containers.filter((c) => !!c.empty === empty && (c.allowedWeightInTon == 20) === twenty).length;
  return {
    totalWeight: containers.reduce((sum, c) => sum + (c?.weight || 0), 0),
    empty20: count(true, true),
    empty40: count(true, false),
    full20: count(false, true),
    full40: count(false, false),
    totalCarts: containers.length,
  };
}

@Component({
  selector: "app-vouchers-print-page",
  templateUrl: "./vouchers-print-page.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VouchersPrintPageComponent implements AfterViewInit {
  readonly voucher = input<TrainVoucher>();

  private readonly splitService = inject<SplitService>("splitService" as never);
  private readonly moment = inject<typeof moment>("moment" as never);

  readonly currentDate = {currentDate: this.moment().valueOf()};
  readonly logoUrl = this.splitService.getSiteLogoUrl();
  readonly displayExternal = this.splitService.getSiteExternalIdConfig();
  readonly weightUnit = this.splitService.getWeightUnit();

  readonly containers = computed(() => (this.voucher() ? toPrintedContainers(this.voucher()) : undefined));
  readonly totals = computed(() => (this.containers() ? toPrintTotals(this.containers()) : undefined));

  ngAfterViewInit(): void {
    setTimeout(() => {
      window.print();
      window.close();
    }, PRINT_DELAY_MS);
  }
}
